import React, { useEffect, useState } from 'react';
import { carteraApi } from '../api';
import { C } from './estilos';

/**
 * "¿A quién le contesta el bot?" · teléfonos de prueba (allowlist).
 *
 * Esto SÍ se guarda: escribe `allowlist` en bot_ventas_configs con
 * POST /bot-ventas/config. Mientras tenga números, el bot SOLO les contesta a
 * esos; vacía, les contesta a todos los leads.
 *
 * Igual que DestinatarioAvisos: sin estado optimista. Lo "guardado" se pinta
 * con lo que respondió el backend.
 */

const DIEZ_DIGITOS = /^\d{10}$/;

const normalizar = (lista) => (Array.isArray(lista) ? lista.map((t) => String(t)) : []);

const mismaLista = (a, b) => a.length === b.length && a.every((t, i) => t === b[i]);

const mensajeDeError = (error, porDefecto) => {
  const respuesta = error?.response;
  if (respuesta?.data?.message) return respuesta.data.message;
  if (!respuesta) return 'No hay conexión con el servidor. Revisa tu red y vuelve a intentar.';
  return porDefecto;
};

export const AllowlistBot = () => {
  const [cargando, setCargando] = useState(true);
  const [guardada, setGuardada] = useState([]);
  const [lista, setLista] = useState([]);
  const [nuevo, setNuevo] = useState('');
  const [errorNuevo, setErrorNuevo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  useEffect(() => {
    const control = new AbortController();
    carteraApi.getBotVentasConfig(control.signal)
      .then(({ data }) => {
        const valores = normalizar(data?.config?.allowlist);
        setGuardada(valores);
        setLista(valores);
      })
      .catch((e) => {
        if (control.signal.aborted) return;
        setError(mensajeDeError(e, 'No se pudo leer la lista de prueba.'));
      })
      .finally(() => {
        if (!control.signal.aborted) setCargando(false);
      });
    return () => control.abort();
  }, []);

  const agregar = () => {
    const tel = nuevo.replace(/\D/g, '');
    if (!DIEZ_DIGITOS.test(tel)) {
      setErrorNuevo('Escribe un teléfono de 10 dígitos.');
      return;
    }
    if (lista.includes(tel)) {
      setErrorNuevo('Ese número ya está en la lista.');
      return;
    }
    setOk(false);
    setErrorNuevo('');
    setLista((l) => [...l, tel]);
    setNuevo('');
  };

  const quitar = (tel) => {
    setOk(false);
    setLista((l) => l.filter((t) => t !== tel));
  };

  const guardar = async () => {
    setGuardando(true);
    setError('');
    setOk(false);
    try {
      const { data } = await carteraApi.actualizarBotVentasAllowlist(lista);
      const valores = normalizar(data?.config?.allowlist);
      setGuardada(valores);
      setLista(valores);
      setOk(true);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo guardar. Intenta de nuevo.'));
    } finally {
      setGuardando(false);
    }
  };

  const sinCambios = mismaLista(lista, guardada);

  return (
    <div aria-label="A quién le contesta el bot" role="group" style={ESTILO.caja}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: C.ink }}>Teléfonos de prueba</span>
        <span style={ESTILO.chipGuarda}>
          <i className="pi pi-check-circle" style={{ fontSize: 11 }} />esto sí se guarda
        </span>
      </div>
      <p style={{ margin: '4px 0 10px', fontSize: 12.5, color: C.ink3, lineHeight: 1.45 }}>
        Mientras haya números aquí, el bot <b>SOLO</b> le contesta a esos (modo pruebas). Vacía = le contesta a todos.
      </p>

      {cargando ? (
        <div style={{ fontSize: 12.5, color: C.gris }}>
          <span className="pi pi-spin pi-spinner" style={{ fontSize: 11, marginRight: 6 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {lista.length === 0 ? (
            <div role="note" style={ESTILO.todos}>
              <i className="pi pi-users" style={{ fontSize: 12 }} />Le contesta a todos los leads
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={ESTILO.pruebas}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: 12 }} />
                Modo pruebas: solo le contesta a {lista.length === 1 ? 'este número' : `estos ${lista.length} números`}
              </div>
              <ul aria-label="Teléfonos de prueba" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {lista.map((tel) => (
                  <li key={tel} style={ESTILO.chipTel}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{tel}</span>
                    <button
                      type="button"
                      aria-label={`Quitar ${tel}`}
                      title="Quitar"
                      onClick={() => quitar(tel)}
                      disabled={guardando}
                      style={ESTILO.quitar}
                    >
                      <i className="pi pi-times" style={{ fontSize: 9 }} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: C.ink2, flex: '1 1 180px' }}>
              Agregar teléfono (10 dígitos)
              <input
                value={nuevo}
                onChange={(e) => { setNuevo(e.target.value); setErrorNuevo(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
                disabled={guardando}
                inputMode="tel"
                placeholder="4421234567"
                style={ESTILO.campo}
              />
            </label>
            <button type="button" onClick={agregar} disabled={guardando} style={{ ...ESTILO.botonSec, marginTop: 20 }}>
              <i className="pi pi-plus" style={{ fontSize: 11, marginRight: 5 }} />Agregar
            </button>
          </div>
          {errorNuevo && <div role="alert" style={{ fontSize: 12, color: '#B42318' }}>{errorNuevo}</div>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={guardar} disabled={guardando || sinCambios} style={{ ...ESTILO.boton, opacity: guardando || sinCambios ? 0.55 : 1 }}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            {!sinCambios && !guardando && <span style={{ fontSize: 12, color: C.naranjaTexto }}>Cambios sin guardar</span>}
            {ok && <span role="status" style={{ fontSize: 12.5, color: C.verde, fontWeight: 600 }}>Guardado</span>}
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#B42318', lineHeight: 1.35 }}>{error}</div>
      )}
    </div>
  );
};

const ESTILO = {
  caja: {
    margin: '6px 4px 12px', padding: '12px 14px', borderRadius: 12,
    background: C.fondo, border: `1px solid ${C.lineaSuave}`,
  },
  chipGuarda: {
    fontSize: 11, color: C.verde, border: `1px solid ${C.verde}`, borderRadius: 10,
    padding: '0 7px', display: 'inline-flex', gap: 4, alignItems: 'center', lineHeight: '18px',
  },
  todos: {
    display: 'flex', gap: 8, alignItems: 'center', padding: '9px 12px', borderRadius: 10,
    background: '#E5F8EF', border: '1px solid #BDEBD3', color: '#0B6B43', fontSize: 13, fontWeight: 700,
  },
  pruebas: {
    display: 'flex', gap: 8, alignItems: 'center', padding: '9px 12px', borderRadius: 10,
    background: C.naranjaFondo, border: '1px solid #F4E2BC', color: '#7A2E0E', fontSize: 13, fontWeight: 700,
  },
  chipTel: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 11px', borderRadius: 999,
    background: '#fff', border: `1px solid ${C.cyanBorde}`, color: C.steel, fontSize: 13, fontWeight: 600,
  },
  quitar: {
    width: 18, height: 18, borderRadius: '50%', border: 'none', background: '#EAF7FC', color: C.steel,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  campo: {
    border: `1px solid ${C.linea}`, borderRadius: 9, padding: '8px 10px', fontSize: 13,
    color: C.ink, background: '#fff', outline: 'none',
  },
  botonSec: {
    border: `1.5px solid ${C.cyanBorde}`, borderRadius: 9, padding: '7px 14px', cursor: 'pointer',
    background: '#fff', color: C.steel, fontSize: 12.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center',
  },
  boton: {
    border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer',
    background: C.steel, color: '#fff', fontSize: 12.5, fontWeight: 700,
  },
};

export default AllowlistBot;
